/**
 * @file PlatformDashboard.tsx
 * @description «Обзор» кабинета платформы (phase08.5; phase19.29 — Commora
 * Console, настоящие данные вместо заглушек).
 *
 * Страница = заголовок + плитки «Пользователи / Интеграции / Последняя
 * копия» + «Требует внимания» + «Быстрые действия» + «Состояние
 * интеграций». Данные — только из уже существующих запросов
 * (`listAdminUsers`, `platformIntegrations.list`, `getBackupConfig` +
 * `listBackupRuns`); у каждого источника свои загрузка/ошибка/«Повторить».
 * Блок без права не показывается и запрос не отправляется — как пункты меню.
 */

import { useMemo } from "react";

import { useCurrentContext, useHasPermission } from "../../auth/hooks";
import { AttentionList } from "../../components/platform/overview/AttentionList";
import { IntegrationStatusList } from "../../components/platform/overview/IntegrationStatusList";
import {
  backupAttention,
  integrationAttention,
  loadBackupOverview,
  type AttentionItem,
} from "../../components/platform/overview/overview-model";
import {
  BackupTile,
  IntegrationsTile,
  UsersTile,
} from "../../components/platform/overview/OverviewTiles";
import { QuickActions } from "../../components/platform/overview/QuickActions";
import { useOverviewSource } from "../../components/platform/overview/use-overview-source";
import { CabinetPageHeader } from "../../components/ui/CabinetPageHeader";
import { listAdminUsers } from "../../lib/api-admin-users";
import { platformIntegrations } from "../../lib/api-platform-integrations";

const ERROR_USERS = "Не удалось загрузить пользователей";
const ERROR_INTEGRATIONS = "Не удалось загрузить интеграции";
const ERROR_BACKUPS = "Не удалось загрузить состояние копий";

export function PlatformDashboard() {
  const canUsers = useHasPermission("PLATFORM_MANAGE_USERS");
  const canIntegrations = useHasPermission("PLATFORM_MANAGE_INTEGRATIONS");
  const canBackups = useHasPermission("PLATFORM_MANAGE_BACKUPS");

  // Активный кабинет: после перечитывания /auth/me (403 «нет активного
  // кабинета» сразу после входа, TD-110) источники перезапрашиваются сами.
  const contextKey = useCurrentContext();
  const users = useOverviewSource(canUsers, listAdminUsers, ERROR_USERS, contextKey);
  const integrations = useOverviewSource(
    canIntegrations,
    platformIntegrations.list,
    ERROR_INTEGRATIONS,
    contextKey,
  );
  const backups = useOverviewSource(canBackups, loadBackupOverview, ERROR_BACKUPS, contextKey);

  const attention = useMemo<AttentionItem[]>(() => {
    const items: AttentionItem[] = [];
    const i = integrations.source;
    const b = backups.source;
    if (i.status === "ready") items.push(...integrationAttention(i.data));
    // Не смогли проверить — так и говорим, а не «Всё в порядке».
    if (i.status === "error") {
      items.push({ id: "integrations-error", tone: "warning", text: `${ERROR_INTEGRATIONS} — откройте раздел`, to: "/platform/integrations" });
    }
    if (b.status === "ready") items.push(...backupAttention(b.data));
    if (b.status === "error") {
      items.push({ id: "backups-error", tone: "warning", text: `${ERROR_BACKUPS} — откройте раздел`, to: "/platform/backups" });
    }
    return items;
  }, [integrations.source, backups.source]);

  const attentionLoading =
    integrations.source.status === "loading" || backups.source.status === "loading";
  const hasTiles = canUsers || canIntegrations || canBackups;

  return (
    <div className="flex flex-col gap-6">
      <CabinetPageHeader
        title="Обзор"
        subtitle="Пользователи, интеграции и резервные копии платформы"
      />

      {hasTiles ? (
        // Нечётная последняя плитка на двух колонках растягивается — без «дырки» на 768.
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:[&>:last-child:nth-child(odd)]:col-span-2 lg:grid-cols-3 lg:[&>:last-child:nth-child(odd)]:col-span-1">
          {canUsers ? <UsersTile source={users.source} onRetry={users.reload} /> : null}
          {canIntegrations ? (
            <IntegrationsTile source={integrations.source} onRetry={integrations.reload} />
          ) : null}
          {canBackups ? <BackupTile source={backups.source} onRetry={backups.reload} /> : null}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="flex flex-col gap-6 lg:col-span-3">
          {canIntegrations || canBackups ? (
            <AttentionList
              items={attention}
              loading={attentionLoading}
              checked={{ integrations: canIntegrations, backups: canBackups }}
            />
          ) : null}
          <QuickActions />
        </div>
        {canIntegrations ? (
          <div className="lg:col-span-2">
            <IntegrationStatusList source={integrations.source} onRetry={integrations.reload} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
